import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { ArrowLeft, PanelRightOpen } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { SecondLevelDrawerCloseContext } from "@/features/navigation/context/SecondLevelDrawerCloseContext";
import { OrganizationLogo } from "@/shared/components/OrganizationLogo";
import { useSidebar } from "@/shared/components/ui/sidebar";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

type SecondLevelDrawerProps = {
  isOpen: boolean
  isSidebarOpen?: boolean
  onClose: () => void
  onBack?: () => void
  children: React.ReactNode
  className?: string
  closeOnLinkClick?: boolean
  mayCloseOnClickOutside?: boolean
  onLinkClick?: () => void
  hideBackButton?: boolean
};

export const SecondLevelDrawer = ({
  isOpen,
  isSidebarOpen = true,
  onClose,
  onBack,
  children,
  className,
  closeOnLinkClick = true,
  mayCloseOnClickOutside = false,
  onLinkClick,
  hideBackButton = false,
}: SecondLevelDrawerProps) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const { setOpenMobile } = useSidebar();

  const handleClose = () => {
    onClose();
    setOpenMobile(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (isOpen && !(event.target as Element).closest("[data-second-level-drawer]")) {
      onClose();
      if (isMobile) {
        setOpenMobile(false);
      }
    }
  };

  // Gérer le rendu avec délai pour l'animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Petit délai pour permettre l'animation d'ouverture
      setTimeout(() => setIsAnimating(true), 10);
    }
    else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Délai correspondant à la durée de l'animation
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mayCloseOnClickOutside) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, mayCloseOnClickOutside]);

  const handleDrawerClick = (e: React.MouseEvent) => {
    if (!closeOnLinkClick) return;

    const target = e.target as Element;
    if (target.closest("a")) {
      // Pour le blog seulement : en desktop, garder le drawer ouvert
      const isBlogRoute = location.pathname.startsWith("/blog");
      if (isBlogRoute && !isMobile) {
        onLinkClick?.();
        return;
      }

      onClose();
      onLinkClick?.();
    }
  };

  if (!shouldRender) return null;

  const drawerContent = (
    <SecondLevelDrawerCloseContext.Provider value={{ closeParentDrawer: handleClose }}>
      <div
        data-second-level-drawer
        className={twMerge(
          "fixed z-50 bg-sidebar text-sidebar-foreground rounded-lg border border-sidebar-border",
          "w-[270px] top-2 bottom-2 left-2 h-[calc(100svh-1rem)] flex flex-col",
          "transform transition-transform duration-300 ease-in-out",
          (isAnimating && isSidebarOpen) ? "translate-x-0" : "-translate-x-[calc(100%+0.5rem)]",
          className,
        )}
      >
        {/* Logo header */}
        <div className="flex flex-col gap-2 px-4 py-3 border-b border-sidebar-border relative">
          {isMobile && (
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-2 right-2 p-2 rounded-md text-sidebar-foreground hover:bg-sidebar-accent transition-colors z-10"
              aria-label="Fermer le menu"
            >
              <PanelRightOpen size={20} />
            </button>
          )}
          <Link
            to="/"
            className="flex items-center justify-center px-2 py-2"
          >
            <OrganizationLogo />
          </Link>
        </div>

        {/* Bouton retour - masqué si hideBackButton est true */}
        {!hideBackButton && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-sidebar-border">
            <button
              onClick={onBack ?? onClose}
              className="flex items-center w-full gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground hover:bg-sidebar-primary/10 hover:text-sidebar-primary dark:hover:bg-sidebar-primary/15 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
          </div>
        )}

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto" onClick={handleDrawerClick}>
          {children}
        </div>
      </div>
    </SecondLevelDrawerCloseContext.Provider>
  );

  // Utiliser createPortal pour rendre le drawer au niveau du body
  return createPortal(drawerContent, document.body);
};

SecondLevelDrawer.displayName = "SecondLevelDrawer";
